// Which part of a cost's VAT each party, and each row of a distribution
// table, carries. One rule for every screen that shows it: the cost popup,
// the cost detail, the settlement summary, the statement and the payment.
//
// A cost left to the settlement passes on exactly the supplier's VAT: the VAT
// of the whole cost is split in whole cents over the parties that carry it,
// each tenant's lease and each unit's owner part (its vacancy and fixed owner
// share together), so they add up to the invoice's VAT. The engine bills that
// split; a table that shows a party on several rows (vacancy intervals, the
// owner share) splits that party's VAT over them.
//
// A cost billed right away sends each tenant a payment request of their own
// (see leaseVat.utils). A tenant without VAT of our own (a residential lease)
// is asked their share plus their part of that same split of the supplier's
// VAT, so the cost asks exactly what the settlement would and the rows show
// the same split. A tenant of a VAT-taxable lease billed at a rate the broker
// chose pays that rate on their share instead: their row shows the VAT of
// that payment (the payment computes it itself), which is no part of the
// supplier's.

import { getAmountWithVat } from "@rentiohq/shared-frontend/dist/utils/number.utils";
import {
  fromCents,
  roundCents,
  splitEqually,
  splitVat,
  toCents,
  vatCents,
} from "./money.utils";
import { Invoice, PaymentSplit } from "./types";

const PERCENT = 100;
const HALF_PERCENT = PERCENT / 2;

// Who carries a row of a cost: a tenant, by lease, or the owner of a unit.
export type TCostPartyKind = "tenant" | "owner";

// How a cost reaches its tenants: through the settlement, or by a payment
// request right away.
export type TCostBilling = "settlement" | "immediate";

export interface ICostVatRow {
  kind: TCostPartyKind;
  // The lease of a tenant row; the unit of an owner row ("" for an owner part
  // that no unit holds).
  partyId: string;
  amount: number;
  // A tenant row billed right away at a VAT rate of our own (a VAT-taxable
  // lease, see leaseVat.utils): its payment adds that VAT to the amount.
  outgoingVatPct?: number;
  // A tenant row read from its stored split: the part of the supplier's VAT
  // its payment asked (PaymentSplit.supplierVat), shown as it was billed.
  supplierVat?: number;
}

export const costPartyKey = (params: {
  kind: TCostPartyKind;
  partyId: string;
}): string => `${params.kind}:${params.partyId}`;

// The VAT of one cost split over the parties that carry it, keyed by
// costPartyKey. Rows of the same party count as one. The parties are split in
// one fixed order, so every screen that splits the same cost over the same
// parties gets the same cents, whatever order it lists them in: a tie cent
// otherwise went to whichever party a screen happened to list first.
// `totalVatCents` is the VAT the parties must add up to when that is not the
// VAT of their amounts together (see splitVat): after a departure settled a
// share of the cost, the invoice's VAT minus what the departure froze.
export const splitVatOverParties = (params: {
  parties: ICostVatRow[];
  vatPct: number;
  totalVatCents?: number;
}): Record<string, number> => {
  const { parties, vatPct, totalVatCents } = params;
  const centsByParty: Record<string, number> = {};
  for (const party of parties) {
    const key = costPartyKey(party);
    centsByParty[key] = (centsByParty[key] ?? 0) + toCents(party.amount);
  }
  const keys = Object.keys(centsByParty).sort();
  const vats = splitVat({
    amounts: keys.map(key => fromCents(centsByParty[key])),
    vatPct,
    totalVatCents,
  });
  return Object.fromEntries(keys.map((key, index) => [key, vats[index]]));
};

// The VAT a payment request adds to a tenant's amount at its own rate: the
// payment computes the amount incl. VAT itself (getAmountWithVat, as the
// legacy payment helper does), so this is that rounding, not the cent rule of
// the settlement.
export const paymentVatOf = (params: {
  amountExclVat: number;
  vatPct: number;
}): number => {
  const { amountExclVat, vatPct } = params;
  return roundCents(
    getAmountWithVat(amountExclVat, vatPct / PERCENT) - amountExclVat,
  );
};

// `vatCentsToSpread` spread over the rows at `indexes`, in whole cents by
// their amounts, written into `vats`. Rows of both signs split per sign, as
// the parties do (see splitVat).
const spreadOverRows = (params: {
  vats: number[];
  rows: ICostVatRow[];
  indexes: number[];
  vatPct: number;
  vatCentsToSpread: number;
}) => {
  const { vats, rows, indexes, vatPct, vatCentsToSpread } = params;
  splitVat({
    amounts: indexes.map(index => rows[index].amount),
    vatPct,
    totalVatCents: vatCentsToSpread,
  }).forEach((vat, position) => {
    vats[indexes[position]] = vat;
  });
};

// The VAT of each row of a cost's distribution table (see the top of this
// file). The rows together hold the whole cost.
export const costRowsVat = (params: {
  rows: ICostVatRow[];
  vatPct: number;
  billing: TCostBilling;
}): number[] => {
  const { rows, vatPct, billing } = params;
  const vats = rows.map(() => 0);

  const vatByParty = splitVatOverParties({ parties: rows, vatPct });
  const indexesByParty: Record<string, number[]> = {};
  rows.forEach((row, index) => {
    (indexesByParty[costPartyKey(row)] ??= []).push(index);
  });
  for (const [key, indexes] of Object.entries(indexesByParty)) {
    spreadOverRows({
      vats,
      rows,
      indexes,
      vatPct,
      vatCentsToSpread: toCents(vatByParty[key]),
    });
  }
  if (billing === "settlement") return vats;

  // The settlement bills a VAT-taxable lease like any other (F.1); a tenant's
  // payment of a cost billed right away bills what its split says.
  rows.forEach((row, index) => {
    if (row.kind !== "tenant") return;
    if (row.outgoingVatPct != null) {
      vats[index] = paymentVatOf({
        amountExclVat: row.amount,
        vatPct: row.outgoingVatPct,
      });
    } else if (row.supplierVat != null) {
      vats[index] = row.supplierVat;
    }
  });
  return vats;
};

// The parties of one property's (or one unit's) part of a cost, as its
// distribution table lists them: its tenants by lease, its vacancy and its
// owner share by unit.
export const prorationVatParties = (params: {
  unitId: string;
  proration: {
    shares: { contractId: string; amount: number }[];
    vacancyIntervals: { amount: number }[];
    ownerBase: number;
  };
}): ICostVatRow[] => {
  const { unitId, proration } = params;
  return [
    ...proration.shares.map(share => ({
      kind: "tenant" as const,
      partyId: share.contractId,
      amount: share.amount,
    })),
    ...proration.vacancyIntervals.map(vacancy => ({
      kind: "owner" as const,
      partyId: unitId,
      amount: vacancy.amount,
    })),
    { kind: "owner" as const, partyId: unitId, amount: proration.ownerBase },
  ];
};

// The supplier's VAT each lease carries of a cost, by contract id: its part
// of the cost's VAT split over all of the cost's parties (`parties`: every
// tenant row and every owner row), exactly as the settlement splits it. A
// tenant billed right away without VAT of our own pays this on top of their
// share, so their payment asks what the settlement would have.
export const supplierVatByLease = (params: {
  parties: ICostVatRow[];
  vatPct: number;
}): Record<string, number> => {
  const { parties, vatPct } = params;
  const vatByParty = splitVatOverParties({ parties, vatPct });
  return Object.fromEntries(
    parties
      .filter(party => party.kind === "tenant")
      .map(party => [party.partyId, vatByParty[costPartyKey(party)] ?? 0]),
  );
};

// The VAT rate of our own each lease's payment was billed at, by contract id
// (see PaymentSplit.outgoingVatPct): the cost detail shows a tenant's row
// with the VAT their payment request asked.
export const outgoingVatPctByLease = (
  splits?: PaymentSplit[],
): Record<string, number> =>
  Object.fromEntries(
    (splits ?? []).flatMap(split =>
      split.contractId && split.outgoingVatPct != null
        ? [[split.contractId, split.outgoingVatPct]]
        : [],
    ),
  );

// The VAT of one invoice the cost popup splits over several properties. Each
// property is saved as a cost of its own and passes on the VAT of its own
// amount (the settlement splits exactly that over its tenants and owner), so
// amounts typed per property can together pass on a cent or two more or less
// than the invoice's VAT: € 1.000,03 at 21% typed as € 333,35 + € 333,34 +
// € 333,34 passes on 3 × € 70,00 against € 210,01. An
// equal split is made so they do not (splitInvoiceEqually). `invoiceVat` is
// the VAT of the invoice itself, also while the parts typed so far do not add
// up to it.
export const splitInvoiceVat = (params: {
  invoiceAmount: number;
  parts: number[];
  vatPct: number;
}): { partsVat: number; invoiceVat: number } => {
  const { invoiceAmount, parts, vatPct } = params;
  return {
    partsVat: fromCents(
      parts.reduce(
        (sum, part) => sum + vatCents({ amountCents: toCents(part), vatPct }),
        0,
      ),
    ),
    invoiceVat: fromCents(
      vatCents({ amountCents: toCents(invoiceAmount), vatPct }),
    ),
  };
};

// The amounts, in cents, that carry exactly `vat` cents of VAT at `vatPct`.
// VAT rounds half up on the cents (vatCents), so that is every amount from
// (100 × vat − 50) / pct up to, but not including, (100 × vat + 50) / pct.
const amountCentsCarryingVat = (params: {
  vat: number;
  vatPct: number;
}): { min: number; max: number } => {
  const { vat, vatPct } = params;
  return {
    min: Math.max(0, Math.ceil((PERCENT * vat - HALF_PERCENT) / vatPct)),
    max: Math.ceil((PERCENT * vat + HALF_PERCENT) / vatPct) - 1,
  };
};

// An invoice split equally over several properties, such that the
// properties' own VAT adds up to exactly the invoice's. A plain split in
// whole cents can pass on a cent or two more or less than the supplier
// invoiced (€ 1.000,03 at 21% over three properties: 3 × € 70,00 against
// € 210,01, see splitInvoiceVat). Then the invoice's VAT is shared out first,
// as evenly as it goes (its remainder cents to the properties whose plain
// part carries the most VAT), and each property gets the amount closest to
// an equal share that carries exactly its part: € 333,36 + € 333,33 +
// € 333,34. The parts stay within a few cents of each other (4 at 21%, 5 at
// 12%, 10 at 6%). A plain split that already adds up is kept as it is.
export const splitInvoiceEqually = (params: {
  amount: number;
  parts: number;
  vatPct: number;
}): number[] => {
  const { amount, parts, vatPct } = params;
  const plain = splitEqually({ amount, parts });
  const amountCents = toCents(amount);
  const vatOf = (cents: number) => vatCents({ amountCents: cents, vatPct });
  const vatOfAll = (partCents: number[]) =>
    partCents.reduce((sum, cents) => sum + vatOf(cents), 0);
  const invoiceVatCents = vatOf(amountCents);
  const plainCents = plain.map(toCents);
  if (
    parts < 2 ||
    vatPct <= 0 ||
    amountCents <= 0 ||
    vatOfAll(plainCents) === invoiceVatCents
  ) {
    return plain;
  }

  // The invoice's VAT over the properties, and the amounts that carry each
  // property's part.
  const lowVatCents = Math.floor(invoiceVatCents / parts);
  const highVatCount = invoiceVatCents - lowVatCents * parts;
  const rangeByPart: { min: number; max: number }[] = [];
  plainCents
    .map((_, index) => index)
    .sort((a, b) => vatOf(plainCents[b]) - vatOf(plainCents[a]) || a - b)
    .forEach((index, rank) => {
      rangeByPart[index] = amountCentsCarryingVat({
        vat: rank < highVatCount ? lowVatCents + 1 : lowVatCents,
        vatPct,
      });
    });

  // Each property's plain part moved into its range, then the cents still to
  // place (or to take back) one at a time where they keep the parts closest
  // to an equal share (the distance in 1/parts of a cent).
  const partCents = plainCents.map((cents, index) =>
    Math.min(rangeByPart[index].max, Math.max(rangeByPart[index].min, cents)),
  );
  const offEqualShare = (cents: number) =>
    Math.abs(parts * cents - amountCents);
  let centsLeft =
    amountCents - partCents.reduce((sum, cents) => sum + cents, 0);
  while (centsLeft !== 0) {
    const step = Math.sign(centsLeft);
    let closest = -1;
    partCents.forEach((cents, index) => {
      const next = cents + step;
      if (next < rangeByPart[index].min || next > rangeByPart[index].max) {
        return;
      }
      if (
        closest < 0 ||
        offEqualShare(next) < offEqualShare(partCents[closest] + step)
      ) {
        closest = index;
      }
    });
    // No room left in any range: does not happen at 6, 12 or 21% over two
    // properties or more, but keep the plain split rather than a wrong one.
    if (closest < 0) return plain;
    partCents[closest] += step;
    centsLeft -= step;
  }
  return vatOfAll(partCents) === invoiceVatCents
    ? partCents.map(fromCents)
    : plain;
};

// A cost billed right away: an immediate payment, or payment requests already
// made for its tenants' shares. Any other cost reaches its tenants through
// the settlement (or was paid outside Rentio).
export const costBillingOf = (
  invoice: Pick<Invoice, "target" | "paymentRequestedAt" | "paymentSplits">,
): TCostBilling => {
  const requested = (invoice.paymentSplits ?? []).some(
    split =>
      !!split.requestedAt ||
      !!split.paymentOrderId ||
      split.paymentRequestId != null,
  );
  return invoice.target === "direct" ||
    !!invoice.paymentRequestedAt ||
    requested
    ? "immediate"
    : "settlement";
};
